/**
 * One definition of what makes two NMI strings the same meter.
 *
 * An AEMO NMI is 10 characters. An 11th character, when present, is the
 * checksum — it carries no identity, and retailers print it inconsistently.
 * Comparing full tokens therefore splits one meter into several.
 *
 * Job 64: Shell invoices were being reported as consolidated (several sites)
 * when they covered a single NMI. Their PDF text layer runs the NMI straight
 * into the next table cell with no separator, so a greedy 10-11 character
 * capture takes that cell's first digit as an 11th character — a different one
 * at each occurrence. "4103898625" appeared as 41038986252 and 41038986256 and
 * looked like two sites. Grouping on the 10-character base collapses them,
 * whatever the trailing character turns out to be.
 *
 * Lives under features/ rather than server/ because BOTH sides need it — the
 * same reason runPool does. The server re-exports it from its old path so every
 * existing import keeps working. Two copies of "are these the same meter" is
 * how the inbox and the screen end up disagreeing about whether a site exists.
 *
 * This module deliberately depends on nothing else — every NMI-handling service
 * imports it, so a cycle here would be hard to unpick.
 */

/** An NMI's identity length. The 11th character, if any, is a checksum. */
export const NMI_BASE_LENGTH = 10;

/**
 * Upper-cased, and zero-padded to 10 for purely numeric NMIs — commonly stored
 * that way, so a candidate read as "233232323" and a site stored as
 * "0233232323" compare equal. Length is otherwise left alone.
 */
export function normalizeNmi(candidate: string): string {
  const upper = candidate.toUpperCase();
  return /^\d+$/.test(upper) ? upper.padStart(NMI_BASE_LENGTH, '0') : upper;
}

/**
 * The 10-character identity of an NMI: truncated past the checksum, then
 * zero-padded and upper-cased. Use this — never the raw token — whenever two
 * NMIs are compared, de-duplicated, or grouped.
 *
 * Truncate-then-pad (not pad-then-truncate) is deliberate: it is the behaviour
 * the NEM matching path has always had as nmiPrefix10, which this now backs.
 */
export function nmiBase10(nmi: string): string {
  return nmi.slice(0, NMI_BASE_LENGTH).padStart(NMI_BASE_LENGTH, '0').toUpperCase();
}

/**
 * AEMO's NMI checksum for a 10-character NMI: from the right, every other
 * character's ASCII code is doubled (the rightmost first), the digits of all
 * the results are summed, and the checksum brings that sum up to a multiple
 * of ten.
 */
export function nmiChecksum(base10: string): number {
  let sum = 0;
  let double = true;
  for (let i = base10.length - 1; i >= 0; i--) {
    let v = base10.charCodeAt(i) * (double ? 2 : 1);
    double = !double;
    while (v > 0) { sum += v % 10; v = Math.floor(v / 10); }
  }
  return (10 - (sum % 10)) % 10;
}

/**
 * Whether a string can be an NMI at all: 10 letters or digits, or 11 where the
 * 11th is the correct checksum. Anything else read off an invoice is a misread
 * (a doubled or dropped character), not a different meter.
 */
export function isPlausibleNmi(raw: string | null | undefined): boolean {
  const s = String(raw ?? '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z0-9]{10,11}$/.test(s)) return false;
  return s.length === 10 || nmiChecksum(s.slice(0, 10)) === Number(s[10]);
}
